// Validation utility functions for form fields

// Email validation
export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!email) {
    return 'Email is required';
  }
  if (!emailRegex.test(email)) {
    return 'Please enter a valid email address';
  }
  return '';
};

// Password validation
export const validatePassword = (password, confirmPassword = null) => {
  if (!password) {
    return 'Password is required';
  }
  if (password.length < 6) {
    return 'Password must be at least 6 characters long';
  }
  if (password.length > 50) {
    return 'Password must not exceed 50 characters';
  }
  // Check for at least one uppercase, one lowercase, one number
  const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/;
  if (!passwordRegex.test(password)) {
    return 'Password must contain at least one uppercase letter, one lowercase letter, and one number';
  }
  if (confirmPassword !== null && password !== confirmPassword) {
    return 'Passwords do not match';
  }
  return '';
};

// Phone number validation
export const validatePhone = (phone) => {
  const phoneRegex = /^[+]?[(]?[0-9]{1,4}[)]?[-\s\.]?[(]?[0-9]{1,4}[)]?[-\s\.]?[0-9]{1,5}[-\s\.]?[0-9]{1,5}$/;
  if (!phone) {
    return 'Phone number is required';
  }
  if (!phoneRegex.test(phone)) {
    return 'Please enter a valid phone number';
  }
  if (phone.length < 10) {
    return 'Phone number must be at least 10 digits';
  }
  return '';
};

// Name validation (first name, last name, etc.)
export const validateName = (name, fieldName = 'Name') => {
  if (!name || !name.trim()) {
    return `${fieldName} is required`;
  }
  if (name.length < 2) {
    return `${fieldName} must be at least 2 characters long`;
  }
  if (name.length > 50) {
    return `${fieldName} must not exceed 50 characters`;
  }
  // Allow only letters, spaces, hyphens, and apostrophes
  const nameRegex = /^[a-zA-Z\s'-]+$/;
  if (!nameRegex.test(name)) {
    return `${fieldName} can only contain letters, spaces, hyphens, and apostrophes`;
  }
  return '';
};

// Required field validation
export const validateRequired = (value, fieldName) => {
  if (!value || (typeof value === 'string' && !value.trim())) {
    return `${fieldName} is required`;
  }
  return '';
};

// OTP validation
export const validateOTP = (otp) => {
  if (!otp) {
    return 'OTP is required';
  }
  if (!/^\d+$/.test(otp)) {
    return 'OTP must contain only numbers';
  }
  if (otp.length !== 6) {
    return 'OTP must be exactly 6 digits';
  }
  return '';
};

// PAN number validation (Indian)
export const validatePAN = (pan) => {
  const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
  if (!pan) {
    return 'PAN number is required';
  }
  if (!panRegex.test(pan.toUpperCase())) {
    return 'Please enter a valid PAN number (e.g., ABCDE1234F)';
  }
  return '';
};

// GSTIN validation (Indian GST number)
export const validateGSTIN = (gstin) => {
  const gstinRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
  if (!gstin) {
    return 'GSTIN is required';
  }
  if (!gstinRegex.test(gstin.toUpperCase())) {
    return 'Please enter a valid GSTIN';
  }
  return '';
};

// TAN number validation (Indian)
export const validateTAN = (tan) => {
  const tanRegex = /^[A-Z]{4}[0-9]{5}[A-Z]{1}$/;
  if (!tan) {
    return 'TAN number is required';
  }
  if (!tanRegex.test(tan.toUpperCase())) {
    return 'Please enter a valid TAN number';
  }
  return '';
};

// URL validation
export const validateURL = (url) => {
  const urlRegex = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
  if (!url) {
    return 'URL is required';
  }
  if (!urlRegex.test(url)) {
    return 'Please enter a valid URL';
  }
  return '';
};

// File validation
export const validateFile = (file, allowedTypes = [], maxSize = 5242880) => { // 5MB default
  if (!file) {
    return 'File is required';
  }
  
  if (allowedTypes.length > 0) {
    const fileType = file.type || '';
    const fileExtension = file.name.split('.').pop().toLowerCase();
    const isValidType = allowedTypes.some(type => 
      fileType.includes(type) || fileExtension === type
    );
    
    if (!isValidType) {
      return `File type not allowed. Allowed types: ${allowedTypes.join(', ')}`;
    }
  }
  
  if (file.size > maxSize) {
    const maxSizeMB = (maxSize / 1048576).toFixed(2);
    return `File size must not exceed ${maxSizeMB}MB`;
  }
  
  return '';
};

// Validate form data based on field configurations
export const validateFormData = (formData, fieldConfigs) => {
  const errors = {};
  
  Object.keys(fieldConfigs).forEach(fieldName => {
    const config = fieldConfigs[fieldName];
    const value = formData[fieldName];
    
    // Check required fields
    if (config.required && (!value || (typeof value === 'string' && !value.trim()))) {
      errors[fieldName] = `${config.label || fieldName} is required`;
      return;
    }
    
    // Skip validation if field is optional and empty
    if (!config.required && (!value || (typeof value === 'string' && !value.trim()))) {
      return;
    }
    
    // Apply specific validators
    if (config.type) {
      let error = '';
      
      switch (config.type) {
        case 'email':
          error = validateEmail(value);
          break;
        case 'password':
          error = validatePassword(value, formData[config.confirmField]);
          break;
        case 'phone':
          error = validatePhone(value);
          break;
        case 'name':
          error = validateName(value, config.label);
          break;
        case 'otp':
          error = validateOTP(value);
          break;
        case 'pan':
          error = validatePAN(value);
          break;
        case 'gstin':
          error = validateGSTIN(value);
          break;
        case 'tan':
          error = validateTAN(value);
          break;
        case 'url':
          error = validateURL(value);
          break;
        case 'file':
          error = validateFile(value, config.allowedTypes, config.maxSize);
          break;
        default:
          if (config.required) {
            error = validateRequired(value, config.label || fieldName);
          }
      }
      
      if (error) {
        errors[fieldName] = error;
      }
    }
    
    // Custom validation function
    if (config.validate && typeof config.validate === 'function') {
      const customError = config.validate(value, formData);
      if (customError) {
        errors[fieldName] = customError;
      }
    }
    
    // Min/Max length validation
    if (value && typeof value === 'string') {
      if (config.minLength && value.length < config.minLength) {
        errors[fieldName] = `${config.label || fieldName} must be at least ${config.minLength} characters`;
      }
      if (config.maxLength && value.length > config.maxLength) {
        errors[fieldName] = `${config.label || fieldName} must not exceed ${config.maxLength} characters`;
      }
    }
    
    // Min/Max value validation for numbers
    if (value && typeof value === 'number') {
      if (config.min !== undefined && value < config.min) {
        errors[fieldName] = `${config.label || fieldName} must be at least ${config.min}`;
      }
      if (config.max !== undefined && value > config.max) {
        errors[fieldName] = `${config.label || fieldName} must not exceed ${config.max}`;
      }
    }
  });
  
  return errors;
};

// Required field indicator component
export const RequiredIndicator = () => (
  <span className="text-red-500 ml-1">*</span>
);

// Error message component
export const ErrorMessage = ({ error, message }) => {
  const errorText = error || message;
  if (!errorText) return null;
  
  return (
    <p className="mt-1 text-sm text-red-600 flex items-center">
      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
      </svg>
      {errorText}
    </p>
  );
};

export default {
  validateEmail,
  validatePassword,
  validatePhone,
  validateName,
  validateRequired,
  validateOTP,
  validatePAN,
  validateGSTIN,
  validateTAN,
  validateURL,
  validateFile,
  validateFormData,
  RequiredIndicator,
  ErrorMessage
};